/**
 * ENG-8269: decide whether a dispatched turn DIED on a transient model-API
 * failure (529 overloaded / 5xx), from Claude Code's own transcript.
 *
 * Sibling of `rate-limit-classifier.ts`, deliberately in the same module so all
 * knowledge of Claude Code's transcript SHAPE lives in one place. That one is
 * about the usage cap (429) and owns it; this one is about everything else that
 * kills a turn, and never classifies 429 (see EXCLUDED_STATUSES).
 *
 * ## Why not the pane
 *
 * The reported incident was an agent sitting on
 * `✳ 529 Overloaded · Retrying in 38s · attempt 10/10` while the user waited.
 * The obvious fix — teach the pane scraper that banner — cannot work. Captured
 * from agt-aws-1: `pane.log` is a raw tmux `pipe-pane` byte stream, and a
 * 12,000-byte window around a live retry banner contained ZERO newlines, 223
 * carriage returns, and the countdown written a character at a time. The line
 * the user sees is assembled by the TERMINAL from many cursor-positioned
 * writes; it never exists contiguously in the file. The nearest contiguous
 * fragment is `"  ⎿  Retrying in 19s · attempt 6/10"` — which does not even
 * carry the status code.
 *
 * This is the third time a parser pinned to Claude Code's TUI has gone quiet
 * (ENG-7904's weekly-limit wording, ENG-7363's `API Error` anchor, ENG-7360's
 * line splitting), and quiet is indistinguishable from healthy. The transcript
 * is a PERSISTENCE format — session resume reads it back — so the vendor pays a
 * cost for breaking it and drift tends to be additive. It is not a stable
 * contract, but it is a far better one, and `scanUnclassifiedErrorKeys` below
 * exists so the day it does drift is a day we hear about.
 *
 * ## The two shapes (captured verbatim from agt-aws-1, 2026-07)
 *
 * Mid-retry — Claude Code is still trying. Note `retryInMs`: this entry is
 * written BEFORE the backoff sleep and BEFORE that attempt runs, so
 * `retryAttempt === maxRetries` means "about to try the last time", NOT
 * "exhausted". Treating it as terminal would announce a death that often does
 * not happen (a real captured episode ran attempts 1→10 over 3m32s):
 *
 *   { "type": "system", "subtype": "api_error", "level": "error",
 *     "error": { "status": 529, "formatted": "529 Overloaded",
 *                "requestId": "req_011Cd1Wi5vcfas7PnvZ2axfj" },
 *     "retryInMs": 555.9, "retryAttempt": 1, "maxRetries": 10,
 *     "timestamp": "2026-07-14T06:56:08.292Z", "isSidechain": false }
 *
 * Terminal — the retries are spent and the error became the turn's OUTPUT. This
 * is the only authoritative "the user is waiting on a reply that is never
 * coming" signal:
 *
 *   { "type": "assistant", "error": "server_error", "isApiErrorMessage": true,
 *     "apiErrorStatus": 529,
 *     "message": { "content": [{ "type": "text",
 *       "text": "API Error: 529 Overloaded. This is a server-side issue..." }] } }
 *
 * `isApiErrorMessage` is what makes this unforgeable: an agent WRITING ABOUT a
 * 529 produces an assistant entry with the same text but no such field — the
 * exact confusion that cost sherlock hours of silence under the old pane-scraped
 * gate (ENG-8198). Never fall back to matching the text.
 *
 * Pure by construction — JSONL text and a time window, no `node:fs`, no path
 * handling — so it stays browser-safe and each consumer keeps its own reader.
 */

/**
 * `failed`   — the newest classified turn died on a transient API error. The
 *              user is waiting on a reply that will never arrive.
 * `retrying` — the newest signal is a mid-retry record: the turn is ALIVE and
 *              Claude Code is still trying. Never a reason to say it failed.
 * `served`   — a real completed turn, so nothing is owed. Ends a watch early.
 * `unknown`  — no classifiable entry in the window.
 */
export type TurnFailureOutcome = 'failed' | 'retrying' | 'served' | 'unknown';

/** Coarse class of the transient failure, for copy selection and telemetry. */
export type TurnFailureClass = 'overloaded' | 'server_error';

export interface TurnFailureClassification {
  outcome: TurnFailureOutcome;
  /** Epoch ms of the classified entry; null when `unknown`. */
  atMs: number | null;
  /** For `failed` / `retrying`: the failure class. Null otherwise. */
  failureClass: TurnFailureClass | null;
  /** The HTTP status carried by the entry, when it had one. */
  httpStatus: number | null;
  /**
   * For `retrying`: which attempt is about to run, and the ceiling. Reported for
   * operator telemetry and for the "still retrying" notice's threshold — NOT as
   * an exhaustion test (see the module docblock).
   */
  attempt: number | null;
  maxAttempts: number | null;
}

/** The "no classifiable entry" result. */
export const UNKNOWN_TURN_FAILURE: TurnFailureClassification = Object.freeze({
  outcome: 'unknown',
  atMs: null,
  failureClass: null,
  httpStatus: null,
  attempt: null,
  maxAttempts: null,
});

/**
 * Statuses this classifier deliberately does NOT own.
 *
 * 429 is the usage cap and belongs to `classifyTranscriptRateLimit`, which
 * reports it with the reset instant parsed out of the refusal. Classifying it
 * here too would double-notify the same user for one event, with worse copy.
 */
const EXCLUDED_STATUSES: ReadonlySet<number> = new Set([429]);

/**
 * Map an HTTP status to a transient failure class, or null when it is not one
 * we act on.
 *
 * Scoped tight to the transient server-side class, matching ENG-6861's rule:
 * auth/billing/quota errors (401/402/403) need an operator, not a "try again",
 * so a notice telling the user to wait would be actively misleading. Excluded by
 * omission rather than by a denylist, so a new 4xx cannot leak in.
 */
export function classifyTransientStatus(status: number): TurnFailureClass | null {
  if (!Number.isFinite(status)) return null;
  if (EXCLUDED_STATUSES.has(status)) return null;
  if (status === 529) return 'overloaded';
  if (status >= 500 && status <= 599) return 'server_error';
  return null;
}

/** Read a finite number off a record, or null. */
function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Is this entry error-SHAPED — i.e. something Claude Code is reporting as a
 * failure, whether or not we understood it?
 *
 * This is the "coarse" half of the drift detector (see
 * {@link scanUnclassifiedErrorKeys}). It is deliberately looser than the
 * classifier: it keys on the generic error markers rather than on the specific
 * fields we read, so a rename of `apiErrorStatus` or `subtype` still counts here
 * while dropping out of the "fine" count — which is exactly the divergence we
 * want to alarm on.
 */
function isErrorShaped(record: Record<string, unknown>): boolean {
  if (record.isApiErrorMessage === true) return true;
  if (record.type === 'system' && record.level === 'error') return true;
  // Fallback: an assistant entry carrying a STRING `error` (e.g. "server_error").
  //
  // Without this the detector is blind to the one rename that matters most. The
  // terminal branch depends on `isApiErrorMessage`; if the vendor renames that
  // field, the entry stops being error-shaped too, so `coarse` never counts it
  // and the alarm never fires for the exact shape this issue is about
  // (CodeRabbit, PR #3907). Kept out of `isShapeRecognised` deliberately — this
  // marks an entry as worth understanding, not as understood.
  if (record.type === 'assistant' && typeof record.error === 'string' && record.error) return true;
  return false;
}

/**
 * Classify one transcript line, or null when it carries no signal.
 *
 * Exported for tests and for consumers that already have lines split; most
 * callers want {@link classifyTranscriptTurnFailure} over the whole file.
 */
export function classifyTurnFailureLine(
  line: string,
  startMs: number,
  endMs: number,
): TurnFailureClassification | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  let obj: unknown;
  try {
    obj = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (typeof obj !== 'object' || obj === null) return null;
  const record = obj as Record<string, unknown>;

  const ts = record.timestamp;
  if (typeof ts !== 'string' || !ts) return null;
  const tsMs = new Date(ts).getTime();
  if (!Number.isFinite(tsMs) || tsMs < startMs || tsMs > endMs) return null;

  return classifyRecord(record, tsMs);
}

/**
 * Classify an already-parsed, already-window-checked entry.
 *
 * Split out so the single-pass {@link analyzeTranscriptTurnFailure} can classify
 * and count drift without parsing each line twice.
 */
function classifyRecord(
  record: Record<string, unknown>,
  tsMs: number,
): TurnFailureClassification | null {
  // A sub-agent's failure is not the user's turn dying. The Task tool runs
  // sidechains inside the same session; letting one notify would tell a user
  // their perfectly healthy turn had failed.
  if (record.isSidechain === true) return null;

  // ---- Mid-retry: `{ type: 'system', subtype: 'api_error' }` ----------------
  if (record.type === 'system' && record.subtype === 'api_error') {
    const error = record.error;
    const status =
      typeof error === 'object' && error !== null
        ? numberOrNull((error as Record<string, unknown>).status)
        : null;
    if (status === null) return null;
    const failureClass = classifyTransientStatus(status);
    if (!failureClass) return null;
    return {
      outcome: 'retrying',
      atMs: tsMs,
      failureClass,
      httpStatus: status,
      attempt: numberOrNull(record.retryAttempt),
      maxAttempts: numberOrNull(record.maxRetries),
    };
  }

  if (record.type !== 'assistant') return null;

  // ---- Terminal: the error became the turn's output ------------------------
  // Keyed on `isApiErrorMessage`, never on the message text. The text alone is
  // forgeable by an agent quoting the error (ENG-8198).
  if (record.isApiErrorMessage === true) {
    const status = numberOrNull(record.apiErrorStatus);
    const failureClass = status === null ? null : classifyTransientStatus(status);
    // An API error we do not act on (the 429 cap, an auth failure) is still not
    // evidence of a healthy turn — drop it rather than letting it read as
    // `served` below. Same trap `rate-limit-classifier.ts` documents.
    if (!failureClass) return null;
    return {
      outcome: 'failed',
      atMs: tsMs,
      failureClass,
      httpStatus: status,
      attempt: null,
      maxAttempts: null,
    };
  }

  // ---- A real completed turn ------------------------------------------------
  // Same positive test as the rate-limit classifier: a genuine model (not the
  // `<synthetic>` placeholder the error path uses) that actually spent tokens.
  // Asking "did any assistant entry happen?" would count the failure itself as
  // evidence of health.
  const message = record.message;
  if (typeof message !== 'object' || message === null) return null;
  const msg = message as Record<string, unknown>;
  if (msg.model === '<synthetic>') return null;
  const usage = msg.usage;
  if (typeof usage !== 'object' || usage === null) return null;
  const u = usage as Record<string, unknown>;
  const spent =
    Number(u.input_tokens ?? 0) +
    Number(u.output_tokens ?? 0) +
    Number(u.cache_creation_input_tokens ?? 0) +
    Number(u.cache_read_input_tokens ?? 0);
  if (!Number.isFinite(spent) || spent <= 0) return null;

  return {
    outcome: 'served',
    atMs: tsMs,
    failureClass: null,
    httpStatus: null,
    attempt: null,
    maxAttempts: null,
  };
}

/**
 * Keep whichever classification is newer. `unknown` loses to anything
 * classified, and ties go to `next` so a later line in the same file wins
 * (transcripts are append-ordered and two entries can share a millisecond).
 */
export function pickNewerTurnFailure(
  current: TurnFailureClassification,
  next: TurnFailureClassification,
): TurnFailureClassification {
  if (next.outcome === 'unknown') return current;
  if (current.outcome === 'unknown') return next;
  // Defensive `?? 0` rather than a non-null assertion: every non-unknown outcome
  // sets atMs today, but a future variant that didn't would silently make
  // `null >= null` false and pin the older result (CodeRabbit, PR #3907).
  return (next.atMs ?? 0) >= (current.atMs ?? 0) ? next : current;
}

/**
 * Classify a whole transcript JSONL, returning the NEWEST classifiable entry in
 * `[startMs, endMs]`.
 *
 * Newest-wins is what makes a long watch window safe: a turn that recovers on
 * retry 7 writes a later `served` entry, which supersedes the `retrying` ones,
 * so a wide window cannot pin a recovered episode open. Never throws.
 */
export function classifyTranscriptTurnFailure(
  jsonl: string,
  startMs: number,
  endMs: number,
): TurnFailureClassification {
  return analyzeTranscriptTurnFailure(jsonl, startMs, endMs).result;
}

/**
 * Could we READ this error entry's shape — regardless of whether we act on it?
 *
 * This is the "fine" test, and the distinction from "did we classify it" is
 * load-bearing. Several entries are understood-and-declined by design: a 429 is
 * the usage cap and belongs to the sibling classifier, and an auth failure is
 * deliberately out of scope. Counting those as drift would make the alarm cry
 * wolf on every capped agent.
 *
 * So: we understood the entry iff we could locate its status field. If Claude
 * Code renames `apiErrorStatus` or `subtype`, this returns false and the
 * divergence fires — which is precisely the event we want to hear about.
 */
function isShapeRecognised(record: Record<string, unknown>): boolean {
  if (record.type === 'system' && record.subtype === 'api_error') {
    const error = record.error;
    return (
      typeof error === 'object' &&
      error !== null &&
      numberOrNull((error as Record<string, unknown>).status) !== null
    );
  }
  if (record.type === 'assistant' && record.isApiErrorMessage === true) {
    return numberOrNull(record.apiErrorStatus) !== null;
  }
  return false;
}

/** Result of the drift scan. See {@link scanUnclassifiedErrorKeys}. */
export interface UnclassifiedErrorScan {
  /** Entries Claude Code marked as errors, however shaped. */
  coarse: number;
  /** Of those, the ones whose shape this classifier could READ (acted on or deliberately declined). */
  fine: number;
  /**
   * Sorted, de-duplicated top-level key names of the error-shaped entries we did
   * NOT understand. This is the payload that makes the alarm actionable: it
   * names the fields the vendor renamed, in the log line, on the day it happens.
   */
  unrecognisedKeys: string[];
}

/**
 * THE ANTI-SILENCE MECHANISM (ENG-8269 AC7).
 *
 * A pinned fixture proves this parser matches 2026's transcript shape forever.
 * It cannot detect the vendor renaming a field — and a parser that silently
 * stops matching is indistinguishable from a healthy fleet, which is the
 * failure this whole issue is an instance of (ENG-7904, ENG-7363, ENG-7360).
 *
 * So count two things over the same pass: how many entries Claude Code flagged
 * as errors AT ALL (`coarse`), and how many of those we could still READ
 * (`fine`, see {@link isShapeRecognised} — acted on or deliberately declined).
 * A caller that sees `coarse > 0 && fine === 0` has positive evidence of drift —
 * real production traffic proving the classifier has gone blind — and emits one
 * log line carrying `unrecognisedKeys` for an alarm to catch.
 *
 * Sidechain entries are skipped entirely rather than counted as drift: a
 * sub-agent's error is a deliberate non-signal, not an unreadable one.
 *
 * Bounded: at most `maxKeys` distinct key names are retained, so a pathological
 * transcript cannot grow this without limit.
 */
export function scanUnclassifiedErrorKeys(
  jsonl: string,
  startMs: number,
  endMs: number,
  opts: { maxKeys?: number } = {},
): UnclassifiedErrorScan {
  const { coarse, fine, unrecognisedKeys } = analyzeTranscriptTurnFailure(
    jsonl,
    startMs,
    endMs,
    opts,
  );
  return { coarse, fine, unrecognisedKeys };
}

/** Classification plus the drift counters, from a SINGLE parse of the JSONL. */
export interface TurnFailureAnalysis extends UnclassifiedErrorScan {
  result: TurnFailureClassification;
}

/**
 * Classify a transcript AND count drift in one pass.
 *
 * The watcher polls the same (potentially tens-of-MB) transcript repeatedly, so
 * parsing it twice — once to classify, once to scan — doubled the cost of the
 * hot path for no benefit (CodeRabbit, PR #3907). `classifyTranscriptTurnFailure`
 * and `scanUnclassifiedErrorKeys` remain as focused wrappers for callers that
 * genuinely want only one half.
 */
export function analyzeTranscriptTurnFailure(
  jsonl: string,
  startMs: number,
  endMs: number,
  opts: { maxKeys?: number } = {},
): TurnFailureAnalysis {
  const maxKeys = opts.maxKeys ?? 40;
  let newest: TurnFailureClassification = UNKNOWN_TURN_FAILURE;
  let coarse = 0;
  let fine = 0;
  const keys = new Set<string>();

  for (const line of jsonl.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    let obj: unknown;
    try {
      obj = JSON.parse(trimmed);
    } catch {
      continue;
    }
    if (typeof obj !== 'object' || obj === null) continue;
    const record = obj as Record<string, unknown>;

    // Window-scope both halves identically so the counts stay comparable.
    const ts = record.timestamp;
    if (typeof ts !== 'string' || !ts) continue;
    const tsMs = new Date(ts).getTime();
    if (!Number.isFinite(tsMs) || tsMs < startMs || tsMs > endMs) continue;

    const classified = classifyRecord(record, tsMs);
    if (classified) newest = pickNewerTurnFailure(newest, classified);

    // A sub-agent's error is a deliberate non-signal, not an unreadable one.
    if (!isErrorShaped(record) || record.isSidechain === true) continue;
    coarse++;
    if (isShapeRecognised(record)) {
      fine++;
      continue;
    }
    if (keys.size < maxKeys) {
      for (const key of Object.keys(record)) {
        if (keys.size >= maxKeys) break;
        keys.add(key);
      }
    }
  }

  return { result: newest, coarse, fine, unrecognisedKeys: [...keys].sort() };
}
